<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'
import AppButton from '@/components/atoms/AppButton'
import { useGlobalStore } from '@/stores/GlobalStore'
import { getVisualViewport, onViewportResize } from '@/helpers/viewport'

/**
 * AppMenuPanel — outer shell for complex dropdown panels.
 *
 * Two modes:
 *
 *   1. Plain panel (default) — visual shell only.
 *      Slots: trigger ({ open }), default, footer ({ close }).
 *      Existing consumers (export menu, publish menu, etc.) sit here.
 *
 *   2. Applyable filter panel (opt-in via `applyable` prop) — adds a
 *      built-in staged-commit value model so each filter dropdown stops
 *      reimplementing the pending → apply → close dance.
 *
 *      Extra props: `value` (v-model:value, scalar or array), `multi`,
 *      `applyLabel`, `cancelLabel`.
 *
 *      Default slot receives `{ pending, toggle, isSelected }` so each
 *      option row wires itself with one click handler:
 *
 *        <AppMenuPanel v-model:open="open" v-model:value="sort" applyable>
 *          <template #trigger="{ open }">…</template>
 *          <template #default="{ toggle, isSelected }">
 *            <button v-for="o in options" @click="toggle(o.value)"
 *                    :class="{ 'bg-blue-50': isSelected(o.value) }">
 *              {{ o.label }}
 *            </button>
 *          </template>
 *        </AppMenuPanel>
 *
 *      Apply emits update:value with the staged value and closes.
 *      Cancel (and click-outside in applyable mode) discards pending and closes.
 *      Opening the panel resets pending from the current committed value.
 *
 *      Override the default footer by providing the `#footer` slot — you get
 *      `{ close, apply, cancel, pending }` to wire custom action rows.
 *
 * Either mode can open on HOVER instead of a click (`hover`), sit BESIDE its
 * trigger (`placement="right-start"`, `distance`) and wear the dark rail's
 * chrome (`dark`) — the new sidebar's flyouts, where a folded group lists its
 * sub-pages next to the rail.
 */
const props = defineProps({
  // ── Shell (existing) ─────────────────────────────────────────────────────
  open:       { type: Boolean, default: false },
  // 'bottom-end' | 'bottom-start' | 'bottom-center' | 'top-end' | 'top-start' | 'top-center' | 'right-start'
  placement:  { type: String,  default: 'bottom-end' },
  /**
   * CSS selector for a different element to position against (portal mode only).
   *
   * Normally the trigger IS the anchor, which is what you want for a dropdown
   * under a button. It stops being what you want when the trigger is one small
   * control inside a larger piece of chrome: the bulk bar's Tags icon opens a
   * 460px panel, and centring on the icon leaves the panel visibly off-centre
   * against the bar it belongs to. Pointing at the bar centres it on the thing
   * the user reads as its parent.
   *
   * Falls back to the trigger when the selector matches nothing, so a renamed
   * class degrades to the old behaviour rather than an unpositioned panel.
   */
  anchor:     { type: String,  default: null },
  width:      { type: String,  default: 'w-72' },
  /**
   * Portal mode: size the panel to the TRIGGER instead of the `width` class.
   *
   * For a panel hanging off a full-width form field, any fixed width is wrong
   * on some screen — and `w-full` is worse than wrong, because a portalled
   * panel is `position: fixed`, so 100% means 100% of the VIEWPORT and the
   * panel spans the whole page. Measuring the trigger is the only thing that
   * makes "as wide as the input" true at every breakpoint.
   */
  matchWidth: { type: Boolean, default: false },
  extraClass: { type: String,  default: '' },
  panelStyle: { type: Object,  default: null },
  /**
   * Teleport the panel to <body> and position it `fixed` from the trigger's
   * rect. Use when the trigger lives inside a stacking context (a `sticky`/
   * `z-*` header, a `transform`ed ancestor) that would otherwise trap the
   * panel beneath fixed page chrome — the bottom pagination bar (`z-100`) or
   * the CDP support widget (`z-[9999]`). zIndex 10000 clears both. Mirrors
   * BaseListbox's `usePortal`.
   *
   * Portal mode also CLAMPS the panel to the viewport — horizontally (never
   * spills past an edge) and vertically (max-height = the space actually
   * available below/above the trigger, body scrolls past it). That keeps a long
   * panel off the fixed bottom-right widgets on short screens.
   */
  portal:     { type: Boolean, default: false },
  /**
   * Open on hover and keyboard focus of the trigger; close once the pointer
   * (or focus) has left both trigger and panel. The panel drives `update:open`
   * itself, and the consumer still owns the state, so it can close on its own
   * cues too — a row clicked, the sidebar unfolding.
   */
  hover:      { type: Boolean, default: false },
  /** Gap between trigger and panel in portal mode, px. */
  distance:   { type: Number,  default: 4 },
  /** The dark rail's chrome: gray-900 on a gray-800 hairline. Rows then take `AppOptionRow dark`. */
  dark:       { type: Boolean, default: false },

  // ── Opt-in staged-commit mode ────────────────────────────────────────────
  applyable:   { type: Boolean, default: false },
  value:       { type: [String, Number, Array, Boolean, null], default: null },
  multi:       { type: Boolean, default: false },
  applyLabel:  { type: String,  default: 'Apply' },
  cancelLabel: { type: String,  default: 'Cancel' },
})

const emit = defineEmits(['update:open', 'update:value'])

// ── Staging state ───────────────────────────────────────────────────────────
const seedPending = () => props.multi
  ? (Array.isArray(props.value) ? [...props.value] : [])
  : props.value

const pending = ref(seedPending())

// ── Portal positioning ───────────────────────────────────────────────────────
const rootRef     = ref(null)
const panelRef    = ref(null)
const portalStyle = ref({})

const computePortalPosition = () => {
  const el = rootRef.value
  if (!el) return
  const anchorEl = props.anchor ? document.querySelector(props.anchor) : null
  const r = (anchorEl || el).getBoundingClientRect()
  const gap = props.distance
  const MARGIN = 8
  // Floor for pathological cases (trigger flush against the viewport edge) —
  // a 20px-tall panel is less usable than one that slightly overhangs.
  const MIN_HEIGHT = 160
  const vw = window.innerWidth
  // zIndex 10000 sits above the pagination bar (z-100) and support widget (9999).
  const style = { position: 'fixed', zIndex: 10000 }

  // Beside the trigger, top edges aligned (the sidebar's flyouts): capped to
  // the space below the trigger, and kept inside the right edge.
  if (props.placement === 'right-start') {
    const besideWidth = panelRef.value?.offsetWidth || 0
    let left = r.right + gap
    if (besideWidth) left = Math.min(left, vw - besideWidth - MARGIN)
    style.top = `${r.top}px`
    style.left = `${Math.max(left, MARGIN)}px`
    const vv = getVisualViewport()
    style.maxHeight = `${Math.max(Math.round((vv.top + vv.height) - r.top - MARGIN), MIN_HEIGHT)}px`
    portalStyle.value = style
    measureCornerOverlap()
    return
  }

  if (props.placement.startsWith('top')) style.bottom = `${window.innerHeight - r.top + gap}px`
  else                                    style.top    = `${r.bottom + gap}px`

  // Anchor to the trigger, then CLAMP so the panel never spills past the
  // viewport — an off-screen fixed panel adds horizontal scroll and visibly
  // shifts the page when it opens (esp. wide mobile panels near a left/mid trigger).
  if (props.matchWidth) style.width = `${Math.round(r.width)}px`

  // The trigger's width when we've just set it — `offsetWidth` still reports
  // the PREVIOUS render on the first open, and the clamps below would then
  // position against a stale number.
  const panelWidth = props.matchWidth ? Math.round(r.width) : (panelRef.value?.offsetWidth || 0)
  // `center` anchors the panel's MIDDLE to the trigger's middle. For a wide
  // panel hanging off one small icon — the bulk bar's Tags button — aligning an
  // edge throws the whole thing to one side of a bar that is itself centred.
  if (props.placement.endsWith('center')) {
    let left = r.left + (r.width / 2) - (panelWidth / 2)
    if (panelWidth) left = Math.min(left, vw - panelWidth - MARGIN)
    style.left = `${Math.max(left, MARGIN)}px`
  } else if (props.placement.endsWith('start')) {
    let left = r.left
    if (panelWidth) left = Math.min(left, vw - panelWidth - MARGIN)
    style.left = `${Math.max(left, MARGIN)}px`
  } else {
    let right = vw - r.right
    if (panelWidth) right = Math.min(right, vw - panelWidth - MARGIN)
    style.right = `${Math.max(right, MARGIN)}px`
  }

  // Clamp VERTICALLY too, for the same reason we clamp horizontally: a long
  // panel (the Columns checklist) on a short viewport ran past the bottom edge,
  // so its last rows were unreachable AND it collided with the fixed support /
  // referral bubbles parked in the bottom-right corner. Capping to the space
  // actually available turns the overflow into an internal scroll.
  //
  // Measured against the VISUAL viewport, not `window.innerHeight`: opening the
  // software keyboard leaves innerHeight untouched while the visible area
  // shrinks, so a panel with a text input in it would size itself to space that
  // is now behind the keyboard. With no keyboard up the two agree, so this
  // changes nothing on desktop.
  const vv = getVisualViewport()
  const available = props.placement.startsWith('top')
    ? r.top - vv.top - gap - MARGIN
    : (vv.top + vv.height) - r.bottom - gap - MARGIN
  style.maxHeight = `${Math.max(Math.round(available), MIN_HEIGHT)}px`

  portalStyle.value = style

  measureCornerOverlap()
}

// True once a max-height is actually in force (portal mode only).
const clamped = computed(() => !!portalStyle.value.maxHeight)

/**
 * The floating support + referral bubbles live in the bottom-right corner,
 * inside shadow-DOM hosts whose z-index is set by third-party scripts — so a
 * panel that reaches down there CANNOT reliably stack above them (see the
 * `batchPreviewOpen` note in GlobalStore). Instead we tell `useCdpSupport` to
 * hide them for as long as this panel overlaps that corner.
 *
 * Measured, not assumed: a short Sort dropdown never reaches the corner and so
 * never makes the bubbles blink out.
 *
 * Store write in a molecule follows the precedent AppPagination set with
 * `paginationBarHeight` — same widget-avoidance contract, same store.
 */
const globalStore = useGlobalStore()

// Widget cluster (support button + Cello badge + gaps), with headroom for the
// pagination bar that shifts them upward.
//   width : right offset 18 + support 48 + gap 24 + Cello 48 = ~138
//   height: bottom offset 18 (or bar 56 + gap 12 = 68) + button 48 = ~116
// Rounded up so a panel that stops just short of the cluster's top edge still
// counts — over-triggering costs a hidden bubble, under-triggering costs the
// user a covered panel, which is the failure being fixed.
const CORNER_W = 200
const CORNER_H = 170

const overlapsCorner = ref(false)

const measureCornerOverlap = async () => {
  await nextTick()

  const el = panelRef.value
  if (!props.open || !el) {
    overlapsCorner.value = false
    return
  }

  const r = el.getBoundingClientRect()
  overlapsCorner.value =
    r.bottom > window.innerHeight - CORNER_H && r.right > window.innerWidth - CORNER_W
}

// Ref-counted so two open overlays can't un-hide the widgets for each other.
const releaseCorner = () => {
  if (!overlapsCorner.value) return
  overlapsCorner.value = false
  globalStore.cornerOverlayCount = Math.max(globalStore.cornerOverlayCount - 1, 0)
}

watch(overlapsCorner, (over, wasOver) => {
  if (over === wasOver) return
  globalStore.cornerOverlayCount = Math.max(globalStore.cornerOverlayCount + (over ? 1 : -1), 0)
})

const onViewportChange = () => { if (props.open && props.portal) computePortalPosition() }

// Returned by onViewportResize — covers keyboard show/hide and pinch-zoom,
// which plain window `resize` does not fire for.
let stopVisualViewport = null

const teardownViewportListeners = () => {
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange, true)
  stopVisualViewport?.()
  stopVisualViewport = null
}

// Reset pending whenever the panel opens — pending represents "what the user
// is staging this session", and we always start it from the current value.
// In portal mode also (re)compute the fixed position + track viewport changes.
watch(() => props.open, async (isOpen) => {
  if (isOpen) {
    pending.value = seedPending()
    if (props.portal) {
      await nextTick()
      computePortalPosition()
      window.addEventListener('resize', onViewportChange)
      window.addEventListener('scroll', onViewportChange, true)
      stopVisualViewport = onViewportResize(onViewportChange)
    }
  } else {
    teardownViewportListeners()
    releaseCorner()
  }
})

onBeforeUnmount(() => {
  teardownViewportListeners()
  // Unmounting while open (route change) must not leave the widgets hidden.
  releaseCorner()
})

const isSelected = (v) => props.multi
  ? Array.isArray(pending.value) && pending.value.includes(v)
  : pending.value === v

const toggle = (v) => {
  if (props.multi) {
    if (!Array.isArray(pending.value)) pending.value = []
    const i = pending.value.indexOf(v)
    if (i >= 0) pending.value.splice(i, 1)
    else pending.value.push(v)
  } else {
    pending.value = v
  }
}

const close = () => emit('update:open', false)

// ── Hover mode ──────────────────────────────────────────────────────────────
// A short show delay keeps a pointer passing down a rail from flashing every
// panel on the way; the hide delay covers the gap between trigger and panel.
const HOVER_SHOW_MS = 80
const HOVER_HIDE_MS = 150
let hoverTimer = null

const clearHoverTimer = () => { clearTimeout(hoverTimer); hoverTimer = null }

const hoverOpen = () => {
  if (!props.hover) return
  clearHoverTimer()
  hoverTimer = setTimeout(() => emit('update:open', true), HOVER_SHOW_MS)
}

const hoverClose = () => {
  if (!props.hover) return
  clearHoverTimer()
  hoverTimer = setTimeout(() => emit('update:open', false), HOVER_HIDE_MS)
}

/** The pointer reached the panel: it stays. */
const hoverStay = () => { if (props.hover) clearHoverTimer() }

const onFocusIn = () => {
  if (!props.hover) return
  clearHoverTimer()
  emit('update:open', true)
}

// Focus moving between trigger and panel keeps it open; leaving both closes it.
const onFocusOut = (event) => {
  if (!props.hover) return
  const next = event.relatedTarget
  if (next && (rootRef.value?.contains(next) || panelRef.value?.contains(next))) return
  emit('update:open', false)
}

onBeforeUnmount(clearHoverTimer)

const apply = () => {
  emit('update:value', props.multi ? [...pending.value] : pending.value)
  close()
}

const cancel = () => {
  // For non-applyable consumers this just closes — seedPending() returns the
  // (unused) initial value and no `update:value` is emitted.
  pending.value = seedPending()
  close()
}

/**
 * Outside clicks: heard in the CAPTURE phase, decided once the click is done.
 *
 * A bubble-phase listener on the document (what this used to be) never heard
 * clicks inside a HeadlessUI dialog — `DialogPanel` stops their propagation — so
 * a panel INSIDE a modal or the Inventory drawer (the "Create tag" colour
 * dropdown, the drawer's tag picker) stayed open wherever you clicked. Capture
 * hears those. But closing right there, in capture, runs BEFORE the clicked
 * element's own handler, and that broke every flow whose click lands outside
 * the panel's DOM yet belongs to it:
 *
 *   - a confirm modal the panel opened (Publish → List Items / End listings):
 *     the panel's close-watcher cleared the chosen marketplaces before the
 *     confirm read them, so nothing was sent;
 *   - content the panel teleports to <body> (Export → All items / Ready
 *     items): closing unmounted the option before its click handler ran.
 *
 * So capture only records the click, and the decision waits until the click
 * has finished dispatching — every handler on the way has run by then. It then
 * closes in exactly the cases the old bubble listener did, plus the one it
 * missed:
 *
 *   - the click reached the document  → nobody claimed it, close;
 *   - it didn't, but it was swallowed by a dialog that CONTAINS this panel
 *     → a click elsewhere in the panel's own modal, close (the case above);
 *   - otherwise something on the way kept it (`@click.stop` in teleported
 *     panel content, a modal layered over the panel) → stay open.
 */
let lastBubbledEvent = null

const markBubbled = (event) => { lastBubbledEvent = event }

const OUTSIDE_EVENTS = ['click', 'touchstart']

OUTSIDE_EVENTS.forEach((type) => document.addEventListener(type, markBubbled))

onBeforeUnmount(() => {
  OUTSIDE_EVENTS.forEach((type) => document.removeEventListener(type, markBubbled))
})

const onOutsideClick = (event) => {
  if (rootRef.value?.contains(event.target)) return

  /**
   * A panel that isn't actually on screen doesn't get to close itself.
   *
   * Some consumers mount the SAME panel twice against one shared open-state —
   * e.g. `ImportCards` renders once in the desktop toolbar and again, with
   * `hideTrigger`, inside the mobile "···" menu. Only one of the two is
   * visible (the other sits under a `hidden`/`xl:hidden` wrapper), but both
   * were listening: opening from the visible trigger immediately tripped the
   * hidden twin's outside-click — the click is outside ITS root — and the menu
   * shut in the same tick.
   *
   * `display: none` collapses the box to 0×0, which `position: fixed`
   * (portal mode) doesn't affect — unlike `offsetParent`, which is null for
   * fixed elements and would misread a perfectly visible portal panel.
   */
  const rect = panelRef.value?.getBoundingClientRect()
  if (!rect?.width && !rect?.height) return

  // Read before the handlers run: one of them may move or remove the target.
  const ownDialog = event.target.closest?.('[role="dialog"]')
  const insideOwnDialog = Boolean(ownDialog?.contains(rootRef.value))

  setTimeout(() => {
    if (lastBubbledEvent === event || insideOwnDialog) cancel()
  })
}

// ── Placement ───────────────────────────────────────────────────────────────
const placementClass = computed(() => {
  const placements = {
    'bottom-end':    'top-full right-0 mt-0.5',
    'bottom-start':  'top-full left-0 mt-0.5',
    'bottom-center': 'top-full left-1/2 -translate-x-1/2 mt-0.5',
    'top-end':       'bottom-full right-0 mb-0.5',
    'top-start':     'bottom-full left-0 mb-0.5',
    'top-center':    'bottom-full left-1/2 -translate-x-1/2 mb-0.5',
    'right-start':   'left-full top-0 ml-0.5',
  }
  return placements[props.placement] ?? placements['bottom-end']
})
</script>

<template>
  <div
    ref="rootRef"
    class="relative"
    @mouseenter="hoverOpen"
    @mouseleave="hoverClose"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <!-- Trigger -->
    <slot name="trigger" :open="open" />

    <!-- Panel — teleported to body in portal mode so a stacking-context-bound
         trigger (sticky header, etc.) can't trap it under fixed page chrome. -->
    <Teleport to="body" :disabled="!portal">
      <Transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <!-- `data-keyboard-scope`: while this panel is open it owns the arrow
             keys (its rows are navigated with them). Page-level handlers bound
             to `document` — the inspector's card paging, for one — check for
             this before acting, so a keystroke meant for the panel doesn't also
             move the page underneath it. -->
        <div
          v-if="open"
          ref="panelRef"
          data-keyboard-scope
          v-click-outside="{ handler: onOutsideClick, events: ['click', 'touchstart'], capture: true }"
          :class="[
            (portal || panelStyle) ? 'fixed' : 'absolute',
            'z-[9999] border rounded-lg overflow-hidden shadow-[0_2px_10px_rgba(9,38,67,0.12)]',
            dark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200',
            // Only when a height cap is in play, so the footer stays pinned
            // while the body scrolls. Off by default — see the body's note.
            clamped ? 'flex flex-col' : '',
            (portal || panelStyle) ? '' : placementClass,
            width, extraClass
          ]"
          :style="portal ? portalStyle : (panelStyle || {})"
          @mouseenter="hoverStay"
          @mouseleave="hoverClose"
          @focusout="onFocusOut"
        >
        <!-- Body — exposes staging helpers when applyable.
             Scrolling is applied ONLY under a height cap: an overflow box clips
             absolutely-positioned descendants whether or not it ends up
             scrolling, which would cut off a nested dropdown opening past the
             panel's edge. Uncapped panels keep their old behaviour.

             `overflow-x-hidden` is not redundant: per CSS, when one axis is
             `visible` and the other isn't, the `visible` one computes to `auto`
             — so `overflow-y-auto` alone was ALSO producing a pointless
             horizontal scrollbar along the panel's bottom edge.

             The vertical bar is there because the content genuinely doesn't
             fit, but it's chrome inside a dropdown, so the thumb stays
             transparent until hover — the list still scrolls, it just doesn't
             read as a second scrollbar. -->
        <div
          class="flex flex-col"
          :class="clamped ? 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden app-menu-panel-scroll' : ''"
        >
          <slot :pending="pending" :toggle="toggle" :is-selected="isSelected" />
        </div>

        <!-- Footer: explicit slot wins; else auto-render when applyable -->
        <div
          v-if="$slots.footer"
          class="flex items-center justify-end gap-2 px-4 pb-2 pt-[10px] border-t border-gray-200"
        >
          <slot name="footer" :close="close" :apply="apply" :cancel="cancel" :pending="pending" />
        </div>
        <div
          v-else-if="applyable"
          class="flex items-center justify-end gap-2 px-4 pb-2 pt-[10px] border-t border-gray-200"
        >
          <AppButton variant="link" size="sm" @click="cancel">{{ cancelLabel }}</AppButton>
          <AppButton variant="default" size="sm" @click="apply">{{ applyLabel }}</AppButton>
        </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
/* Slim, quiet scrollbar for the clamped panel body — visible on hover,
   invisible at rest so a capped dropdown doesn't read as a scroll region. */
.app-menu-panel-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}
.app-menu-panel-scroll:hover {
  scrollbar-color: #D1D5DB transparent;   /* gray-300 */
}
.app-menu-panel-scroll::-webkit-scrollbar {
  width: 6px;
}
.app-menu-panel-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 9999px;
}
.app-menu-panel-scroll:hover::-webkit-scrollbar-thumb {
  background-color: #D1D5DB;
}
</style>
